import difference from 'lodash/difference';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useDeleteJobLocationsByIds } from 'src/hooks/jobs/delete-job-locations-by-ids.hook';
import { useDeleteJobSupervisorsByIds } from 'src/hooks/jobs/delete-job-supervisors-by-ids.hook';
import { useUpsertJob } from 'src/hooks/jobs/upsert-job.hook';
import { JobForm } from 'src/pages/jobs/components/job-form';
import { IJob } from 'src/pages/jobs/jobs.interfaces';

import { JobEditWrapper } from './job-details-edit.styles';

const JobDetailsEdit = ({
  initialValues,
  onCancel,
}: {
  initialValues: IJob;
  onCancel: () => void;
}) => {
  const currentCompanyId = useGetCurrentCompanyId();
  const upsertJob = useUpsertJob();

  const deleteJobLocationsByIds = useDeleteJobLocationsByIds();
  const deleteJobSupervisorsByIds = useDeleteJobSupervisorsByIds();

  const onFinish = (values: any) => {
    const deletedJobLocationsIds = initialValues.jobLocations
      .filter(({ _id }) => {
        const resultJobLocationsIds = values.jobLocations.map(({ _id }: any) => _id);

        return !resultJobLocationsIds.includes(_id);
      })
      .map(({ _id }: any) => _id);

    const deletedJobSupervisorsIds = values.jobLocations
      .filter(({ _id }: any) => _id)
      .flatMap(({ _id, jobSupervisors: resultJobSupervisors }: any) => {
        const initialJobSupervisors = initialValues.jobLocations.find(
          ({ _id: initialId }: any) => initialId === _id && !deletedJobLocationsIds.includes(_id)
        )?.jobSupervisors as [];

        if (!initialJobSupervisors.length) {
          return null;
        }

        return difference(
          initialJobSupervisors.map(({ _id }: any) => _id),
          resultJobSupervisors.map(({ _id }: any) => _id)
        );
      })
      .filter(Boolean);

    const payload = {
      ...values,
      _id: initialValues._id,
      platformCompanyId: currentCompanyId,
      documents: values.documents.map((doc: any) => ({
        documentLabelId: doc.documentLabelId,
        documentTemplatesIds: doc.documentTemplatesIds,
      })),
      jobLocations: values.jobLocations.map((jobLocation: any) => ({
        _id: jobLocation?._id,
        companyLocationId: jobLocation.companyLocationId,
        companyBusinessAddressId: jobLocation.companyBusinessAddressId,
        companyDepartmentId: jobLocation.companyDepartmentId,

        jobSupervisors: jobLocation.jobSupervisors.map((jobSupervisor: any) => ({
          _id: jobSupervisor._id,
          supervisorJobTitle: jobSupervisor.supervisorJobTitle,
          supervisorMemberId: jobSupervisor.supervisorMemberId,
          jobPlacesCount: jobSupervisor.jobPlacesCount,
        })),
      })),
    };

    upsertJob({
      variables: { payload },
      onCompleted: () => {
        onCancel();
        if (deletedJobLocationsIds.length) {
          deleteJobLocationsByIds({
            variables: { jobLocationsIds: deletedJobLocationsIds, jobId: initialValues._id },
          });
        }
        if (deletedJobSupervisorsIds.length) {
          deleteJobSupervisorsByIds({
            variables: { jobSupervisorsIds: deletedJobSupervisorsIds, jobId: initialValues._id },
          });
        }
      },
    });
  };

  return (
    <JobEditWrapper>
      <JobForm
        onCancel={onCancel}
        onFinish={onFinish}
        initialValues={{
          ...initialValues,
          documents: initialValues.documents.map(doc => ({
            ...doc,
            documentsTemplates: doc.documentTemplates.map(docTemplate => docTemplate._id),
          })),
          jobLocations: initialValues.jobLocations.map(location => ({
            ...location,
            jobSupervisors: location.jobSupervisors.map(supervisor => ({
              ...supervisor,
              employees: [],
            })),
          })),
        }}
      />
    </JobEditWrapper>
  );
};

export default JobDetailsEdit;
